import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Strip locale prefix from redirect (e.g., /lt/pricing -> /pricing)
  const rawRedirect = searchParams.get('redirect') || '/home';
  const redirectTo = rawRedirect.replace(/^\/(en|es|ru|lt)\//, '/');
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  // Set language from URL param (?lang=en) - used when coming from Next.js landing
  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (langParam && ['en', 'es', 'ru', 'lt'].includes(langParam) && langParam !== i18n.language) {
      i18n.changeLanguage(langParam);
    }
  }, [searchParams, i18n]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo);
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Login successful - useEffect will handle redirect
    } catch (err: any) {
      // Extract error message from different possible response formats
      let errorMessage = t('auth.login.error_default');

      if (err.response?.data?.message) {
        // NestJS error format
        const backendMessage = Array.isArray(err.response.data.message)
          ? err.response.data.message.join(', ')
          : err.response.data.message;

        // Translate common backend messages
        if (backendMessage === 'Invalid credentials') {
          errorMessage = t('auth.login.error_invalid_credentials');
        } else {
          errorMessage = backendMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 pt-16 sm:pt-0 pb-8">
      {/* Language Switcher - Fixed top right */}
      <div className="fixed top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600/80 to-secondary-600/80 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all flex items-center space-x-2 shadow-lg"
          >
            <span className="text-lg">🌐</span>
            <span className="uppercase font-bold">{i18n.language}</span>
            <span className={`text-xs transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}>▼</span>
          </button>
          {isLangMenuOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-dark-900 border border-white/20 rounded-lg shadow-2xl z-50 animate-scale-in">
              {[
                { code: 'es', name: '🇪🇸 Español' },
                { code: 'en', name: '🇬🇧 English' },
                { code: 'ru', name: '🇷🇺 Русский' },
                { code: 'lt', name: '🇱🇹 Lietuvių' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg transition-all ${
                    i18n.language === lang.code
                      ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white font-semibold'
                      : 'text-gray-300'
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-md w-full">
        <div className="text-center mb-6 animate-fade-in">
          <Link to="/" className="inline-flex items-center justify-center mb-3 hover:opacity-80 transition-opacity">
            <img src="/landing/logo-light.svg" alt="Fonorex" className="h-16" />
          </Link>
          <p className="text-gray-400 text-base sm:text-lg px-4">{t('auth.login.title')}</p>
        </div>

        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 animate-scale-in">
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-xl backdrop-blur-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                {t('auth.login.email')}
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                inputMode="email"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-500"
                placeholder={t('auth.login.email_placeholder')}
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-300">
                  {t('auth.login.password')}
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  {t('auth.login.forgot_password')}
                </Link>
              </div>
              <input
                id="password"
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-500"
                placeholder={t('auth.login.password_placeholder')}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-glow"
            >
              {loading ? t('auth.login.button_loading') : t('auth.login.button')}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-400">
            {t('auth.login.no_account')}{' '}
            <Link to={`/register${redirectTo !== '/home' ? `?redirect=${redirectTo}` : ''}`} className="text-primary-400 hover:text-primary-300 font-semibold transition-colors">
              {t('auth.login.signup_link')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
